import { useState } from "react";

import { FormDialog } from "@/common/components/form-dialog/FormDialog";
import { assertUnreachable } from "@/common/helper/commonHelper";
import { useTranslation } from "@/translations";

import { PlanningMode } from "./PlanAttendanceDialog.types";
import { MultiDayForm } from "./components/multi-day-form/MultiDayForm";
import { useMultiDayForm } from "./components/multi-day-form/hooks/useMultiDayForm";
import { WeeklyPlanningForm } from "./components/weekly-planning-form/WeeklyPlanningForm";
import { useWeeklyPlanningForm } from "./components/weekly-planning-form/hooks/useWeeklyPlanningForm";
import { WeeklySeriesForm } from "./components/weekly-series-form/WeeklySeriesForm";
import { useWeeklySeriesForm } from "./components/weekly-series-form/hooks/useWeeklySeriesForm";

interface Props {
  onClose: () => void;
  initialWeekStart: Date;
}

export function PlanAttendanceDialog({ onClose, initialWeekStart }: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(PlanningMode.Weekly);

  const weeklyForm = useWeeklyPlanningForm(initialWeekStart, onClose, mode === PlanningMode.Weekly);
  const seriesForm = useWeeklySeriesForm(onClose);
  const multiDayForm = useMultiDayForm(onClose);

  const activeFormik =
    mode === PlanningMode.Weekly
      ? weeklyForm.formik
      : mode === PlanningMode.WeeklySeries
        ? seriesForm.formik
        : multiDayForm.formik;
  const isSubmitting = activeFormik.isSubmitting;

  const renderContent = () => {
    switch (mode) {
      case PlanningMode.Weekly:
        return <WeeklyPlanningForm {...weeklyForm} onModeChange={setMode} />;
      case PlanningMode.WeeklySeries:
        return <WeeklySeriesForm {...seriesForm} onModeChange={setMode} />;
      case PlanningMode.MultiDay:
        return <MultiDayForm {...multiDayForm} onModeChange={setMode} />;
      default:
        return assertUnreachable(mode);
    }
  };

  return (
    <FormDialog
      title={t("calendar.planAttendance.title")}
      onClose={onClose}
      formik={activeFormik}
      submitLabel={t("common.save")}
      submitDisabled={isSubmitting}
      paperSx={{ maxWidth: { lg: "90%" } }}
    >
      {renderContent()}
    </FormDialog>
  );
}
